import { observer } from "mobx-react";
import { IAppliedFilterChipComponent } from "../BaseChipComponents/IAppliedFilterChipComponent";
import { useStore } from "utils/useStore";
import React, { useCallback } from "react";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { formatSegmentHeading } from "components/UI/AcxTranscription/utils";
import { allOutlineSegmentOptions } from "components/Conversations/Views/Drawer/components/DeepFilter/OutlineFilterStore";
import {
    chipAggregatedLabel,
    chipItemLabel,
    renderCollectionChips,
    useChipFiltersStore,
} from "../BaseChipComponents/chipUtils";

const sortByOutlineOrder = (segmentIds: string[]): string[] => {
    const orderMap = new Map(
        allOutlineSegmentOptions.map((opt, i) => [opt.id, i]),
    );
    return [...segmentIds].sort(
        (a, b) => (orderMap.get(a) ?? 999) - (orderMap.get(b) ?? 999),
    );
};

export const OutlineChips = observer(function (
    props: IAppliedFilterChipComponent,
) {
    const applicationFiltersStore = useChipFiltersStore(props);
    const messageStore = useStore(MessageStore);

    const removeSegment = useCallback(
        (segmentId: string) => {
            const success =
                applicationFiltersStore.removeOutlineSegmentById(segmentId);

            if (!success) {
                messageStore.logError(
                    "Error removing outline segment. Segment not found.",
                );
            }
        },
        [messageStore, applicationFiltersStore],
    );

    const filterItems = applicationFiltersStore.outlineSegmentFilterItems;

    const containsSegments = filterItems
        .filter((item) => item.value)
        .flatMap((item) => item.segments);

    const notContainsSegments = filterItems
        .filter((item) => !item.value)
        .flatMap((item) => item.segments);

    if (containsSegments.length === 0 && notContainsSegments.length === 0)
        return null;

    const removeAllContains = () => {
        containsSegments.forEach((id) => {
            applicationFiltersStore.removeOutlineSegmentById(id);
        });
    };

    const removeAllNotContains = () => {
        notContainsSegments.forEach((id) => {
            applicationFiltersStore.removeOutlineSegmentById(id);
        });
    };

    const renderSegmentGroup = (
        segmentIds: string[],
        isExclusion: boolean,
    ) =>
        renderCollectionChips({
            items: sortByOutlineOrder(segmentIds),
            disabled: props.disabled,
            isApplicable: props.isApplicable,
            getKey: (segmentId) =>
                `outline-${isExclusion ? "not-contains" : "contains"}-${segmentId}`,
            getLabel: (segmentId) =>
                chipItemLabel(
                    "Outline",
                    formatSegmentHeading(segmentId),
                    isExclusion,
                ),
            getTooltipLabel: formatSegmentHeading,
            getAggregatedLabel: (count) =>
                chipAggregatedLabel(count, "Outline Segments", isExclusion),
            onRemoveAll: isExclusion
                ? removeAllNotContains
                : removeAllContains,
            onRemoveItem: removeSegment,
        });

    return (
        <>
            {renderSegmentGroup(containsSegments, false)}
            {renderSegmentGroup(notContainsSegments, true)}
        </>
    );
});
